// @flow

// ----------------------------------------------------------------------
// <copyright file="Izenda.Paginator.jsx" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
// </copyright>
// ----------------------------------------------------------------------

import React, { Component, PropTypes } from 'react';
import { observable } from 'mobx';
import ReactDOM from 'react-dom';
import connect from 'share/enhancers/Izenda.Connect';
import DropDownList from 'share/components/controls/Izenda.DropDownList';
import elementResizeDetector from 'share/utils/Izenda.SizeDetector';
import classnames from 'classnames';
import action from './Izenda.PaginatorAction';
import Promise from 'bluebird';
import { map } from 'lodash/fp';
import { PAGING_SIZE_OPTIONS as PAGE_SIZES } from 'logic/data/Izenda.UIConstants';

const SMALL_WIDTH = 325;

@connect(action)
class Paginator extends Component {
  static displayName = 'Paginator';

  @observable
  paginatorStatus = {
    pageIndexInputVal: this.props.pageIndex,
    isSmall: false
  };

  static propTypes = {
    totalRecords: PropTypes.number.isRequired,
    pageSize: PropTypes.number.isRequired,
    pageSizes: PropTypes.array,
    pageIndex: PropTypes.number.isRequired,
    onChange: PropTypes.func.isRequired,
    onDropdownChange: PropTypes.func.isRequired,
    className: PropTypes.string,
    canChangePageSize: PropTypes.bool
  };

  static defaultProps = {
    canChangePageSize: true
  };

  componentWillReceiveProps(nextProps: Object) {
    const { pageIndex } = nextProps;
    if (pageIndex) {
      this.paginatorStatus.pageIndexInputVal = pageIndex;
    }
  }

  componentDidMount() {
    this.addListener();
  }

  componentWillUnmount() {
    this.removeListener();
  }

  /**
   * Go to specific page
   * @param {Number} page
   */
  goTo(page: Number) {
    const {
      props,
      props: { pageIndex }
    } = this;

    props.confirmDirtyForm(props.isDirty && props.isDirty()).then(() => {
      if (page !== pageIndex) {
        Promise.resolve(props.onChange(page)).then(() => {
          this.paginatorStatus.pageIndexInputVal = page;
        });
      }
    });
  }

  _onClickFirst = () => {
    const { totalPage } = this.props;
    if (totalPage > 0) {
      this.goTo(1);
    }
  };

  _onClickLast = () => {
    const { totalPage } = this.props;
    if (totalPage > 0) {
      this.goTo(totalPage);
    }
  };

  _onClickNext = () => {
    const { pageIndex, totalPage } = this.props;

    if (pageIndex < totalPage) {
      this.goTo(pageIndex + 1);
    }
  };

  _onClickPrev = () => {
    const { pageIndex } = this.props;

    if (pageIndex > 1) {
      this.goTo(pageIndex - 1);
    }
  };

  _onPageNumberChange = (e: Event) => {
    this.paginatorStatus.pageIndexInputVal = e.target.value;
  };

  _onKeyPress = (e: Event) => {
    if (e.key === 'Enter') {
      const page = parseInt(e.target.value, 10) || 1;
      const { totalPage } = this.props;

      this.paginatorStatus.pageIndexInputVal = page;

      if (page >= 1 && page <= totalPage) {
        this.goTo(page);
      }
    }
  };

  _onDropdownChange = (obj: Object) => {
    const { props } = this;

    return props.confirmDirtyForm(props.isDirty && props.isDirty()).then(() => {
      return Promise.resolve(props.onDropdownChange(parseInt(obj.value, 10))).then(() => {
        this.paginatorStatus.pageIndexInputVal = 1;
      });
    });
  };

  addListener() {
    this.domNode &&
      elementResizeDetector.listenTo(this.domNode, element => {
        if (element.clientWidth < SMALL_WIDTH) {
          !this.paginatorStatus.isSmall && (this.paginatorStatus.isSmall = true);
        } else {
          this.paginatorStatus.isSmall && (this.paginatorStatus.isSmall = false);
        }
      });
  }

  setDomNode = (component: Object) => {
    this.domNode = component;
  };

  removeListener() {
    this.domNode && elementResizeDetector.removeAllListeners(this.domNode);
  }

  renderNoRecordFound() {
    const { localization, className } = this.props;
    const classes = this.getPaginatorClass(className);
    return (
      <nav className={classes}>
        <span>{localization.getText('COMMON_MESSAGES_NO_RECORD_FOUND')}</span>
      </nav>
    );
  }

  /**
   * Get class name for navigate buttons
   * @param {Boolean} isForwardButtons
   */
  getButtonsClassName(isForwardButtons: Boolean) {
    const { pageIndex: currentPage, totalPage } = this.props;

    return classnames({
      disabled: currentPage === (isForwardButtons ? totalPage : 1)
    });
  }

  getPaginatorClass(className: String) {
    return classnames('paginator', className);
  }

  renderPaging() {
    const {
      localization,
      totalPage,
      canChangePageSize,
      beginRecord,
      endRecord,
      className,
      totalRecords
    } = this.props;
    const classes = this.getPaginatorClass(className);
    const { isSmall } = this.paginatorStatus;

    const PAGINATOR_ITEMS_PER_PAGE_TEXT =
      !canChangePageSize || isSmall
        ? localization.getText('PAGINATOR_WITHOUT_ITEMS_PER_PAGE')
        : localization.getText('PAGINATOR_ITEMS_PER_PAGE');

    const itemPerPagesText = String.format(
      PAGINATOR_ITEMS_PER_PAGE_TEXT,
      beginRecord,
      endRecord,
      totalRecords
    );

    return (
      <nav className={classes} ref={this.setDomNode}>
        <div>
          {itemPerPagesText}
          {!isSmall && canChangePageSize && this.renderItemPerPageControl()}
        </div>
        <div className='paginate'>
          {isSmall && canChangePageSize && this.renderItemPerPageControl()}
          <a onClick={this._onClickFirst} className={this.getButtonsClassName(false)}>
            <i className='icon-step-backward' />
          </a>
          <a onClick={this._onClickPrev} className={this.getButtonsClassName(false)}>
            <i className='icon-triangle-left' />
          </a>
          <input
            type='text'
            pattern='[0-9]*'
            value={this.paginatorStatus.pageIndexInputVal}
            onChange={this._onPageNumberChange}
            onKeyPress={this._onKeyPress}
          />
          / {totalPage}
          <a onClick={this._onClickNext} className={this.getButtonsClassName(true)}>
            <i className='icon-triangle-right' />
          </a>
          <a onClick={this._onClickLast} className={this.getButtonsClassName(true)}>
            <i className='icon-step-forward' />
          </a>
        </div>
      </nav>
    );
  }

  getPageSizeOptions(values: Array) {
    return map(val => {
      return { value: val };
    }, values);
  }

  renderItemPerPageControl() {
    const { pageSizes, pageSize } = this.props;
    return (
      <DropDownList
        className='u_marginRight'
        options={this.getPageSizeOptions(pageSizes || PAGE_SIZES)}
        value={pageSize}
        labelField='value'
        valueField='value'
        onChange={this._onDropdownChange}
        ref='searchDropdown'
      />
    );
  }

  render() {
    const { totalRecords } = this.props;

    if (!totalRecords) {
      return null;
    }

    return totalRecords !== 0 ? this.renderPaging() : this.renderNoRecordFound();
  }
}

export default Paginator;


// WEBPACK FOOTER //
// ./ui/share/components/controls/DataGrid/helpers/Izenda.Paginator.js